import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewContainerRef} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, FormGroupDirective} from '@angular/forms';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {BehaviorSubject, Observable, of, shareReplay, switchMap, take, tap} from 'rxjs';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';

import {TerraUtils} from '../../../../shared/TerraUtils';
import {StorageService} from '../../../../services/storage.service';
import {ResourceService} from '../../../../services/resource.service';
import {PlaidService} from '../../../../services/shared/plaid.service';
import {PlaidOAuth} from '../../../../shared/models/OAuth/PlaidOAuth.model';
import {AppSettingsService} from '../../../../services/app-settings.service';
import {PlaidDataService} from '../../../../services/shared/plaid-data.service';
import {LpPaymentRequestService} from '../../../lp-top-notification/lp-payment-request.service';
import {PendingPaymentRequestResponse} from '../../../../shared/models/PendingPaymentRequestResponse.model';
import {TransferUsingPlaidDialogComponent, TransferUsingPlaidParams} from '../../../../shared/components/unit/transfer-using-plaid-dialog/transfer-using-plaid-dialog.component';
import {DeliveryMethod} from '../../../../dashboard/shared/holding/capital-call/models';
import {LoggerService} from '../../../../shared/errors/logger.service';
import {SnackbarService} from 'src/app/services/snackbar.service';
import OrderStatus from 'src/app/shared/enums/OrderStatus.enum';
import {ImpersonationService} from '../../../../services/shared/impersonation.service';

@Component({
  selector: 'terra-lp-capital-call-details',
  templateUrl: './lp-capital-call-details.component.html',
  styleUrls: ['./lp-capital-call-details.component.scss'],
  providers: [PlaidService, PlaidDataService, FormGroupDirective],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LpCapitalCallDetailsComponent extends OnDestroyMixin implements OnInit {
  @Input() paymentRequest: PendingPaymentRequestResponse;
  @Output() achDebitPaymentRequestCompleted = new EventEmitter();
  USIso = TerraUtils.consts.countryCode.US;
  allCountries = this.resourceService.allCountries.sort((c1, c2) => {
    if (c1.code === this.USIso) {
      return -1;
    }

    return 0;
  });

  pageForm: UntypedFormGroup;
  paymentDeliveryMethods$: Observable<DeliveryMethod>;
  isLoadingDeliveryMethods$ = new BehaviorSubject(false);

  DeliveryMethod = DeliveryMethod;
  TerraUtils = TerraUtils;

  constructor(private dialog: MatDialog,
    private plaidService: PlaidService,
    private vcr: ViewContainerRef,
    private storageService: StorageService,
    private appSetting: AppSettingsService,
    private paymentRequestService: LpPaymentRequestService,
    private resourceService: ResourceService,
    private fb: UntypedFormBuilder,
    private logger: LoggerService,
    private snackbar: SnackbarService,
    private impersonationService: ImpersonationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.generatePageForm();
    this.subscribeToCountryUpdate();
    const continueOAuthData = this.storageService.getPersistent<PlaidOAuth>(TerraUtils.consts.OAUTH_LOCAL_STORAGE_KEY);
    if (!!continueOAuthData && continueOAuthData.transferIdentification === this.paymentRequest.paymentToken) {
      this.plaidService.handlePlaidOAuthRedirect(this.openFundCommitmentDialog.bind(this));
    }
  }

  generatePageForm() {
    this.pageForm = this.fb.group({
      countryId: null
    });
  }

  get payAppUrl(): string {
    if (this.isViewingAsInvestor) {
      return '';
    } else if (!!this.paymentRequest.orderId && this.paymentRequest.orderStatus === OrderStatus.NotSubmittedByUser) {
      return `${this.appSetting.payAppFrontEnvironmentAppBaseUrl}dashboard`;
    }
    return `${this.appSetting.payAppFrontEnvironmentAppBaseUrl}account/PaymentRequest/${this.paymentRequest.paymentToken}`;
  }

  get isViewingAsInvestor(): boolean {
    return this.impersonationService.isViewingAsInvestor;
  }

  downloadPdf() {
    this.isLoadingDeliveryMethods$.next(true);
    this.paymentRequestService.downloadPaymentRequestPdf(this.paymentRequest.paymentToken)
      .pipe(take(1))
      .subscribe(
        isDownloaded => {
          this.isLoadingDeliveryMethods$.next(false);
          this.snackbar.showGeneralMessage('File Downloaded');
        },
        err => {
          this.isLoadingDeliveryMethods$.next(false);
        }
      );
  }

  /**
   * Opens a dialog to func commitment through ACH debit
   * @param continueOAuthFlow To continue OAuth flow
   */
  public openFundCommitmentDialog(continueOAuthFlow: boolean = false) {
    const dialogConfig = new MatDialogConfig<TransferUsingPlaidParams>();
    dialogConfig.disableClose = true;
    dialogConfig.viewContainerRef = this.vcr;
    dialogConfig.data = {
      unitBankAccountId: this.paymentRequest.destinationUnitBankAccountId,
      unitBankAccountName: this.paymentRequest.destinationUnitBankAccountName,
      holdingId: this.paymentRequest.holdingId,
      continueOAuthFlow,
      amount: this.paymentRequest.receiveAmount,
      lockAmount: true,
      title: 'Fund Capital Call',
      amountStepButtonText: 'FUND COMMITMENT',
      paymentToken: this.paymentRequest.paymentToken,
      displayAchDebitLimits: false,
      paymentDescription: 'C.Call',
      paymentAddenda: 'Capital Call',
      accountHolderDefault: this.paymentRequest.investingEntity.name
    };

    this.dialog.open(TransferUsingPlaidDialogComponent, dialogConfig)
      .afterClosed()
      .pipe(take(1))
      .subscribe(completed => {
        if (completed) {
          this.achDebitPaymentRequestCompleted.next(undefined);
        }
      });
  }

  private subscribeToCountryUpdate() {
    this.paymentDeliveryMethods$ = this.pageForm.get('countryId').valueChanges.pipe(
      untilComponentDestroyed(this),
      switchMap(countryId => {
        if (!!countryId && typeof countryId === 'number') {
          this.isLoadingDeliveryMethods$.next(true);
          return this.paymentRequestService.getDeliveryMethodsForCountry(this.paymentRequest.paymentToken, countryId);
        }
        else {
          return of(DeliveryMethod.None);
        }
      }),
      tap(deliveryMethod => {
        this.isLoadingDeliveryMethods$.next(false);
        if (deliveryMethod === DeliveryMethod.None && !!this.pageForm.get('countryId').value && typeof this.pageForm.get('countryId').value === 'number') {
          this.logger.error(`No delivery method received for payment token: ${this.paymentRequest.paymentToken} and country code: ${this.pageForm.get('countryId').value}`);
        }
      }),
      shareReplay(1)
    );
  }
}
